import { Box, Button, Slider, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { percentage } from "../../shared/percentage";
import { CurrencyTextField } from "../general/CurrencyTextField";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { DialogShell } from "../general/DialogShell";
import { SectionLabel } from "../general/SectionLabel";
import { QUIET_SLIDER } from "../general/tableStyles";
import { QuantumProps } from "./QuantumCell";

const QuantumLiabilityEditor = ({
  quantum,
  defendant,
  liability = 0,
  onLiabilityChange,
}: {
  quantum: QuantumProps;
  defendant: string;
  liability: number;
  onLiabilityChange: (newLiability: number) => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.quantums.liability.editDialog",
  });
  const { currency } = useCurrency();

  const [sliderValue, setSliderValue] = useState(liability * 100);
  // use to force rerender of currency field
  const [sliderTs, setSliderTs] = useState(Date.now());

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <div>
        <SectionLabel>{t("subTitle", { ...quantum, defendant })}</SectionLabel>
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {t("hint", { ...quantum, defendant })}
        </Typography>
      </div>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: "none", minWidth: 72 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, lineHeight: 1.2, ...TABULAR_NUMERALS }}
          >
            {percentage.format(sliderValue / 100)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("sliderLabel")}
          </Typography>
        </Box>
        <Slider
          size="small"
          value={sliderValue}
          aria-label={t("sliderLabel")}
          // the share stands beside the slider and follows it
          valueLabelDisplay="off"
          onChangeCommitted={(ignored, value) => {
            if (typeof value !== "number") return;
            setSliderValue(value);
            setSliderTs(Date.now());
            onLiabilityChange(value / 100);
          }}
          onChange={(ignored, value) => {
            if (typeof value !== "number") return;
            setSliderTs(Date.now());
            setSliderValue(value);
          }}
          sx={{ flex: 1, ...QUIET_SLIDER }}
        />
      </Box>
      <CurrencyTextField
        key={"liability_" + sliderTs}
        currency={currency}
        label={t("textFieldLabel")}
        variant="outlined"
        size="small"
        onValueChange={(newValue) => {
          setSliderValue((newValue / quantum.quantumAmount) * 100);
          onLiabilityChange(newValue / quantum.quantumAmount);
        }}
        min={0}
        max={quantum.quantumAmount}
        value={(quantum.quantumAmount * sliderValue) / 100}
        sx={{ maxWidth: 240 }}
      />
    </Box>
  );
};

export const QuantumLiabilityEditorDialog = ({
  quantum,
  defendant,
  liability = 0,
  open,
  onClose,
}: {
  quantum: QuantumProps;
  defendant: string;
  liability: number;
  open: boolean;
  onClose: (newLiability?: number) => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.quantums.liability.editDialog",
  });
  const { currency } = useCurrency();
  const [newLiability, setNewLiability] = useState(liability);

  // what the liability is for: the issue and the claimed amount
  const subjects = [
    {
      label: t("issueLabel", { ...quantum, defendant }),
      text: t("issueDescription", { ...quantum }),
    },
    {
      label: t("quantumLabel", { ...quantum }),
      text: t("quantumDescription", {
        ...quantum,
        quantumAmount: currency.format(quantum.quantumAmount),
      }),
    },
  ];

  return (
    <DialogShell
      open={open}
      onClose={() => onClose()}
      title={t("title", { ...quantum, defendant })}
      actions={
        <>
          <Button onClick={() => onClose()} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => onClose(newLiability)}
            variant="contained"
            autoFocus
          >
            {t("submit")}
          </Button>
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          bgcolor: "background.default",
        }}
      >
        {subjects.map(({ label, text }) => (
          <div key={label}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {text}
            </Typography>
          </div>
        ))}
      </Box>
      <QuantumLiabilityEditor
        defendant={defendant}
        quantum={quantum}
        liability={liability}
        onLiabilityChange={(value) => setNewLiability(value)}
      />
    </DialogShell>
  );
};
